import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Camera, ArrowRight, IdentificationCard, UserCircle, Moon, Sun, Bank, Clock, ShieldCheck, PencilSimple } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_da191abd-9276-45a7-bd48-1757c1f4e471/artifacts/p5urxzpf_diametor_logo_black__1_-removebg-preview.png";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_debt-cycle/artifacts/gzyrfqiy_diametor_logo_white-removebg-preview.png";

export default function VerificationPage() {
  const { user, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const govtIdRef = useRef(null);
  const selfieRef = useRef(null);

  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [bank, setBank] = useState({ account_number: '', ifsc: '', bank_name: '', branch: '' });
  const [govtIdPreview, setGovtIdPreview] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [govtIdFile, setGovtIdFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [verStatus, setVerStatus] = useState(null);
  const [bankEdit, setBankEdit] = useState(false);

  useEffect(() => {
    axios.get(`${API}/verification/status`, { withCredentials: true })
      .then(res => setVerStatus(res.data)).catch(() => {});
  }, []);

  const handleCapture = (type) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === 'govt') { setGovtIdPreview(reader.result); setGovtIdFile(file); }
      else { setSelfiePreview(reader.result); setSelfieFile(file); }
    };
    reader.readAsDataURL(file);
  };

  const setB = (k) => (e) => setBank({ ...bank, [k]: e.target.value });

  const uploadFile = async (file) => {
    const form = new FormData();
    form.append('file', file);
    const res = await axios.post(`${API}/profile/upload-photo`, form, {
      withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data.path;
  };

  const validateBank = () => {
    for (const [k, label] of [['account_number', 'Account number'], ['ifsc', 'IFSC'], ['bank_name', 'Bank name'], ['branch', 'Branch']]) {
      if (!bank[k]?.trim()) { toast.error(`${label} is required`); return false; }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (!govtIdFile) { toast.error('Government ID photo is required'); return; }
    if (!selfieFile) { toast.error('Selfie with Government ID is required'); return; }
    if (!validateBank()) return;
    setSubmitting(true);
    try {
      const govtPath = await uploadFile(govtIdFile);
      const selfiePath = await uploadFile(selfieFile);
      await axios.post(`${API}/verification/submit`, {
        name: name.trim(), phone, full_name: name.trim(),
        govt_id_path: govtPath, selfie_path: selfiePath, ...bank,
      }, { withCredentials: true });
      toast.success('Submitted for manual verification!');
      await refreshUser();
      const res = await axios.get(`${API}/verification/status`, { withCredentials: true });
      setVerStatus(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  const handleBankEditSubmit = async () => {
    if (!validateBank()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/verification/submit`, {
        name: name.trim(), phone, full_name: name.trim(), bank_only: true, ...bank,
      }, { withCredentials: true });
      toast.success('Bank details submitted for re-verification!');
      await refreshUser();
      const res = await axios.get(`${API}/verification/status`, { withCredentials: true });
      setVerStatus(res.data); setBankEdit(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  const status = verStatus?.verification?.status;
  const bankVerified = verStatus?.bank_verified;

  const Header = () => (
    <>
      <button onClick={toggleTheme} className={`fixed top-4 right-4 z-50 p-2.5 rounded-sm border ${isDark ? 'border-zinc-700 bg-zinc-900 text-zinc-400' : 'border-zinc-300 bg-white text-zinc-600'}`} data-testid="ver-theme-toggle">
        {isDark ? <Sun size={18} weight="bold" /> : <Moon size={18} weight="bold" />}
      </button>
      <div className={`py-4 px-6 border-b ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
        <img src={isDark ? LOGO_DARK : LOGO_LIGHT} alt="Diametor" className="h-8" />
      </div>
    </>
  );

  const bankFields = () => (
    <>
      <div className="mb-5"><label className={lbl}>Account Holder Name *</label>
        <Input value={name} onChange={e => setName(e.target.value)} className={inp} data-testid="ver-name-input" /></div>
      <div className="mb-5"><label className={lbl}>Bank Account Number *</label>
        <Input value={bank.account_number} onChange={setB('account_number')} placeholder="XXXXXXXXXXXX" inputMode="numeric" className={inp} data-testid="kyc-account_number" /></div>
      <div className="mb-5"><label className={lbl}>IFSC Code *</label>
        <Input value={bank.ifsc} onChange={setB('ifsc')} placeholder="e.g. HDFC0001234" className={inp} data-testid="kyc-ifsc" /></div>
      <div className="mb-5"><label className={lbl}>Bank Name *</label>
        <Input value={bank.bank_name} onChange={setB('bank_name')} placeholder="e.g. HDFC Bank" className={inp} data-testid="kyc-bank_name" /></div>
      <div className="mb-6"><label className={lbl}>Branch *</label>
        <Input value={bank.branch} onChange={setB('branch')} placeholder="e.g. MG Road, Bengaluru" className={inp} data-testid="kyc-branch" /></div>
    </>
  );

  const lbl = `font-ibm text-xs font-semibold uppercase tracking-[0.2em] mb-2 block ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`;
  const inp = `h-11 rounded-sm font-ibm ${isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-50' : 'bg-zinc-50 border-zinc-200 text-zinc-950'}`;

  // PENDING
  if (status === 'pending') {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-zinc-950' : 'bg-white'}`} data-testid="verification-pending-page">
        <Header />
        <div className="max-w-lg mx-auto p-6 md:p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-yellow-400/10 flex items-center justify-center mx-auto mb-4"><Clock size={30} weight="bold" className="text-yellow-400" /></div>
          <h1 className={`font-outfit text-2xl font-bold mb-2 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Verification Pending</h1>
          <p className={`font-ibm text-sm mb-2 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Your ID, selfie and bank details are under manual review.</p>
          <p className={`font-ibm text-xs mb-6 px-4 py-2 rounded-sm inline-block ${isDark ? 'bg-zinc-900 text-zinc-400' : 'bg-zinc-100 text-zinc-600'}`}>This can take up to <strong>5 business days</strong>. Status stays "Pending" until approved.</p>
          <div><button onClick={() => navigate('/dashboard')} className="h-10 px-6 bg-yellow-400 text-zinc-950 font-ibm font-semibold text-sm rounded-sm hover:bg-yellow-500" data-testid="back-to-dashboard">Back to Dashboard</button></div>
        </div>
      </div>
    );
  }

  // APPROVED -> show details + edit bank
  if (bankVerified && status === 'approved' && !bankEdit) {
    const v = verStatus.verification || {};
    return (
      <div className={`min-h-screen ${isDark ? 'bg-zinc-950' : 'bg-white'}`} data-testid="verification-approved-page">
        <Header />
        <div className="max-w-lg mx-auto p-6 md:p-8">
          <div className={`p-3 mb-6 border rounded-sm flex items-center gap-2 ${isDark ? 'border-green-800 bg-green-500/10' : 'border-green-200 bg-green-50'}`} data-testid="kyc-verified-banner">
            <ShieldCheck size={18} weight="bold" className="text-green-500" />
            <span className={`font-ibm text-sm font-medium ${isDark ? 'text-green-400' : 'text-green-700'}`}>KYC Verified</span>
          </div>
          <h2 className={`font-outfit text-lg font-semibold mb-3 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Your Bank Details</h2>
          <div className={`p-4 rounded-sm border space-y-2 mb-4 ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`}>
            <Row label="Account Holder" value={v.full_name || name} isDark={isDark} />
            <Row label="IFSC" value={v.ifsc} isDark={isDark} />
            <Row label="Bank" value={v.bank_name} isDark={isDark} />
            <Row label="Branch" value={v.branch} isDark={isDark} />
          </div>
          <button onClick={() => setBankEdit(true)} className={`w-full h-11 font-ibm text-sm font-semibold rounded-sm border flex items-center justify-center gap-2 ${isDark ? 'border-zinc-700 text-zinc-200 hover:border-yellow-400/50' : 'border-zinc-200 text-zinc-800 hover:border-yellow-400/50'}`} data-testid="edit-bank-btn"><PencilSimple size={15} weight="bold" /> Edit Bank Account (re-verification required)</button>
          <button onClick={() => navigate('/dashboard')} className={`w-full mt-3 h-10 font-ibm text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  // BANK EDIT mode (reuses existing ID/selfie)
  if (bankEdit) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-zinc-950' : 'bg-white'}`} data-testid="bank-edit-page">
        <Header />
        <div className="max-w-lg mx-auto p-6 md:p-8">
          <h1 className={`font-outfit text-2xl font-bold mb-1 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Edit Bank Account</h1>
          <p className={`font-ibm text-sm mb-6 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Updating bank details requires manual re-verification (up to 5 business days). Your ID stays the same.</p>
          {bankFields()}
          <button onClick={handleBankEditSubmit} disabled={submitting} className="w-full h-12 bg-yellow-400 text-zinc-950 font-ibm font-semibold text-sm rounded-sm hover:bg-yellow-500 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="submit-bank-edit">
            {submitting ? <div className="animate-spin w-5 h-5 border-2 border-zinc-950 border-t-transparent rounded-full" /> : <><ArrowRight size={16} weight="bold" /> Submit for Re-verification</>}
          </button>
          <button onClick={() => setBankEdit(false)} className={`w-full mt-3 h-10 font-ibm text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>Cancel</button>
        </div>
      </div>
    );
  }

  // FRESH / REJECTED submission flow
  return (
    <div className={`min-h-screen ${isDark ? 'bg-zinc-950' : 'bg-white'}`} data-testid="verification-page">
      <Header />
      <div className="max-w-lg mx-auto p-6 md:p-8">
        <h1 className={`font-outfit text-2xl sm:text-3xl font-bold tracking-tight mb-1 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>KYC Verification</h1>
        <p className={`font-ibm text-sm mb-6 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Verify your identity & bank account to be included in cycles and receive payouts.</p>
        {status === 'rejected' && (
          <div className={`p-3 mb-6 border rounded-sm ${isDark ? 'border-red-900 bg-red-500/10' : 'border-red-200 bg-red-50'}`} data-testid="ver-rejected-banner">
            <span className={`font-ibm text-xs ${isDark ? 'text-red-400' : 'text-red-600'}`}>Your previous submission was rejected. Please re-submit with correct details.</span>
          </div>
        )}

        {/* Step 1: ID */}
        <p className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-3 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>Step 1 · Identity (Camera Only)</p>
        <div className="mb-6">
          <label className={lbl}>Government ID Photo *</label>
          <div onClick={() => govtIdRef.current?.click()} className={`w-full h-40 border-2 border-dashed rounded-sm flex flex-col items-center justify-center cursor-pointer ${isDark ? 'border-zinc-700 hover:border-yellow-400/50 bg-zinc-900' : 'border-zinc-300 hover:border-yellow-400/50 bg-zinc-50'}`} data-testid="govt-id-capture-area">
            {govtIdPreview ? <img src={govtIdPreview} alt="Govt ID" className="h-full w-full object-contain p-2" /> : <><IdentificationCard size={32} className={isDark ? 'text-zinc-600 mb-2' : 'text-zinc-400 mb-2'} /><p className={`font-ibm text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>Tap to capture Government ID</p></>}
          </div>
          <input ref={govtIdRef} type="file" accept="image/*" capture="environment" onChange={handleCapture('govt')} className="hidden" data-testid="govt-id-input" />
        </div>
        <div className="mb-8">
          <label className={lbl}>Selfie with Government ID *</label>
          <div onClick={() => selfieRef.current?.click()} className={`w-full h-40 border-2 border-dashed rounded-sm flex flex-col items-center justify-center cursor-pointer ${isDark ? 'border-zinc-700 hover:border-yellow-400/50 bg-zinc-900' : 'border-zinc-300 hover:border-yellow-400/50 bg-zinc-50'}`} data-testid="selfie-capture-area">
            {selfiePreview ? <img src={selfiePreview} alt="Selfie" className="h-full w-full object-contain p-2" /> : <><UserCircle size={32} className={isDark ? 'text-zinc-600 mb-2' : 'text-zinc-400 mb-2'} /><p className={`font-ibm text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>Tap to capture Selfie with ID</p></>}
          </div>
          <input ref={selfieRef} type="file" accept="image/*" capture="user" onChange={handleCapture('selfie')} className="hidden" data-testid="selfie-input" />
        </div>

        {/* Step 2: Bank */}
        <p className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-3 flex items-center gap-1 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}><Bank size={14} weight="bold" /> Step 2 · Bank Account (for payout)</p>
        {bankFields()}

        <div className={`p-3 mb-5 rounded-sm border ${isDark ? 'border-zinc-700 bg-zinc-900/50' : 'border-zinc-200 bg-zinc-50'}`}>
          <p className={`font-ibm text-xs flex items-start gap-2 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}><Clock size={14} className="text-yellow-400 shrink-0 mt-0.5" /> This goes for <strong>manual review</strong> and can take up to <strong>5 business days</strong>. Your status will show "Pending" until verified.</p>
        </div>

        <button onClick={handleSubmit} disabled={submitting} className="w-full h-12 bg-yellow-400 text-zinc-950 font-ibm font-semibold text-sm rounded-sm hover:bg-yellow-500 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50" data-testid="submit-verification">
          {submitting ? <div className="animate-spin w-5 h-5 border-2 border-zinc-950 border-t-transparent rounded-full" /> : <><ArrowRight size={16} weight="bold" /> Submit for Verification</>}
        </button>
        <button onClick={() => navigate('/dashboard')} className={`w-full mt-3 h-10 font-ibm text-sm ${isDark ? 'text-zinc-500 hover:text-zinc-300' : 'text-zinc-400 hover:text-zinc-600'}`} data-testid="cancel-verification">Cancel</button>
      </div>
    </div>
  );
}

function Row({ label, value, isDark }) {
  return (
    <div className="flex justify-between">
      <span className={`font-ibm text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{label}</span>
      <span className={`font-ibm text-xs font-medium ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>{value || '—'}</span>
    </div>
  );
}
